<template>
    <div class="login-form-cmp">
        <div class="login-form">
            <div :class="['header-title', { isMobile: isMobile }]">
                欢迎登录智能体开发平台
            </div>
            <div class="header-subtitle">请登录后继续使用</div>

            <v-form
                kind="desc"
                ref="form"
                :model="form"
                @submit="(e) => e.preventDefault()"
            >
                <v-form-item :rules="rules.phone" name="phone">
                    <v-input
                        prefix="+86"
                        v-model="form.phone"
                        autofocus
                        placeholder="请输入手机号"
                        class="phone-input"
                    ></v-input>
                </v-form-item>
                <v-form-item :rules="rules.verifyCode" name="verifyCode">
                    <div class="verify-code">
                        <v-input
                            v-model="form.verifyCode"
                            placeholder="请输入验证码"
                            class="code-input"
                        ></v-input>
                        <v-button
                            :loading="sending"
                            :disabled="disableSendCode"
                            @click="debouncedSendVerifyCode"
                            class="code-btn"
                            size="small"
                        >
                            <span
                            >{{ countdown ? "重新发送" : "发送验证码"
                            }}<span v-if="countdown">({{ countdown }}s)</span></span
                            >
                        </v-button>
                    </div>
                </v-form-item>
                <v-button
                    @click="login"
                    kind="primary"
                    size="large"
                    class="login-btn"
                    :loading="loading"
                >登录</v-button
                >
                <v-status-bar
                    class="login-error"
                    v-if="loginErrorMsg"
                    kind="serious-warning"
                >
                    <span>{{ loginErrorMsg }}</span>
                </v-status-bar>
                <v-checkbox @change="agreed = !agreed" :checked="agreed">
                    <div class="privacy-policy">
                        我已阅读并同意
                        <v-button
                            size="medium"
                            type="link"
                            kind="primary"
                            @click="goPrivacyPolicy(SERVICE_PROTOCOL)"
                        >
                            服务条款
                        </v-button>
                        和
                        <v-button
                            size="medium"
                            type="link"
                            kind="primary"
                            @click="goPrivacyPolicy(PRIVATE_PROTOCOL)"
                        >
                            隐私政策
                        </v-button>
                    </div>
                </v-checkbox>
            </v-form>
        </div>
        <div class="login-copyright"></div>
    </div>
</template>

<script>
import _ from 'lodash';
import { getCaptchaAppId, getQueryVariable } from '@/utils';
import {
    PHONE_NUM_REGEX,
    TIME_COUNT,
    SERVICE_PROTOCOL,
    PRIVATE_PROTOCOL
} from '@/constants/common';

export default {
    name: 'LoginForm',
    data () {
        return {
            SERVICE_PROTOCOL,
            PRIVATE_PROTOCOL,
            loading: false,
            sending: false,
            agreed: false,
            countdown: 0,
            loginErrorMsg: '',
            timer: null, // 计时器
            form: {
                phone: '',
                verifyCode: ''
            },
            rules: {
                phone: [
                    {
                        required: true,
                        message: '请输入手机号',
                        trigger: 'change,input,blur'
                    },
                    { validate: PHONE_NUM_REGEX, message: '请输入正确的手机号' }
                ],
                verifyCode: [
                    {
                        required: true,
                        message: '请输入验证码',
                        trigger: 'change,input,blur'
                    }
                ]
            }
        };
    },
    computed: {
        disableSendCode () {
            return (
                this.sending ||
                !!this.countdown ||
                !this.form.phone ||
                !PHONE_NUM_REGEX.test(this.form.phone)
            );
        }
    },
    beforeDestroy () {
        clearInterval(this.timer);
        this.timer = null;
    },
    methods: {
        startCountdown () {
            this.countdown = TIME_COUNT;
            clearInterval(this.timer);
            this.timer = setInterval(() => {
                this.countdown -= 1;
                if (this.countdown <= 0) {
                    this.countdown = 0;
                    clearInterval(this.timer);
                    this.timer = null;
                }
            }, 1000);
        },
        debouncedSendVerifyCode: _.debounce(function () {
            this.sendVerifyCode();
        }, 200),
        debouncedLogin: _.debounce(function () {
            this.login();
        }, 200),
        async sendVerifyCode () {
            this.loginErrorMsg = '';
            if (!this.form.phone) {
                this.loginErrorMsg = '请输入手机号';
                return;
            }
            if (!PHONE_NUM_REGEX.test(this.form.phone)) {
                this.loginErrorMsg = '请输入正确的手机号';
                return;
            }

            try {
                this.sending = true;
                const captchaCallback = async function (result = {}) {
                    try {
                        // 协议参考：https://iwiki.woa.com/p/1794654729
                        const { ret, ticket, randstr, errorCode, errorMessage } = result;
                        if (ret === 0) {
                            await this.$ajax.post('SendVerifyCodeNew', {
                                telephone: this.form.phone,
                                sms_type: 'cloud', // 走腾讯云的短信发送
                                captcha_ticket: {
                                    is_verify: true,
                                    ticket,
                                    rand_str: randstr
                                }
                            });
                            this.startCountdown();
                        } else if (errorCode) {
                            throw new Error(`[${errorCode}]: ${errorMessage}`);
                        }
                    } catch (error) {
                        console.log(error);
                        this.loginErrorMsg = error.message || '验证码发送失败，请重试';
                    }
                    this.sending = false;
                };
                // eslint-disable-next-line no-undef
                const captcha = new TencentCaptcha(
                    getCaptchaAppId(),
                    captchaCallback.bind(this)
                );
                captcha.show();
            } catch (error) {
                this.sending = false;
                console.error('拉起图形验证码出错', error);
                this.loginErrorMsg = error.message || '未知错误';
            }
        },
        async login () {
            this.loginErrorMsg = '';
            // 仅仅用于触发表单校验
            this.$refs.form.submit();
            if (!this.form.phone || !this.form.verifyCode) {
                this.loginErrorMsg = '请输入手机号和验证码';
                return;
            }
            if (!PHONE_NUM_REGEX.test(this.form.phone)) {
                this.loginErrorMsg = '请输入正确的手机号';
                return;
            }
            if (!this.agreed) {
                this.loginErrorMsg = '请先阅读并同意用户协议和隐私策略';
                return;
            }

            this.loading = true;

            try {
                const { user_name, user_avatar } = await this.$ajax.post('Login', {
                    telephone: this.form.phone,
                    code: this.form.verifyCode,
                    is_agreed: this.agreed,
                    user_type: 1 // 说明是腾讯云知识引擎的体验用户，与企点渠道区分
                });
                this.loading = false;
                localStorage.setItem('user_name', user_name);
                localStorage.setItem('user_avatar', user_avatar);
                const redirectUri = decodeURIComponent(getQueryVariable('redirect_url'));
                if (redirectUri) {
                    window.location.href = redirectUri;
                    window.location.reload();
                } else {
                    const { protocol, host, pathname } = window.location;
                    window.location.href = `${protocol}//${host}${pathname}#/`;
                    window.location.reload();
                }
            } catch (error) {
                this.loading = false;
                console.log('login error', error);
                this.loginErrorMsg = error.msg || '登录异常，请重试';
            }
        },
        goPrivacyPolicy (url) {
            window.open(url);
        }
    }
};
</script>

<style lang="less" scoped>
.login-form {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  input {
    font-size: 16px;
    -webkit-text-size-adjust: none;
  }

  .v-form-item {
    margin-bottom: 0;

    /deep/ .v-form-item__input {
      width: 100%;
    }
    /deep/ .v-description-item--text-content {
      width: 0;
      margin-right: 0;
    }
    /deep/ .v-form-item__bterr {
      margin-top: -15px;
    }
  }

  /deep/ .v-description-item--value {
    padding-bottom: 0;
  }

  .header-title {
    font: var(--display-bold);
    color: var(--color-text-primary);
    text-align: center;
    margin-bottom: 16px;

    &.isMobile {
      font: var(--headline-bold);
    }
  }

  .header-subtitle {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-text-caption);
    text-align: center;
    margin-bottom: 56px;
  }

  .phone-input {
    width: 100%;
    height: 40px;
    font-size: 14px;
    border-radius: 4px;
    margin-bottom: 20px;

    /deep/ .v-input--prefix span {
      line-height: 40px;
    }
  }

  .code-input {
    width: 58%;
    height: 40px;
    font-size: 14px;
    border-radius: 4px;
    margin-bottom: 18px;
  }

  .code-input.v-input {
    margin-top: 0 !important;
  }

  .code-btn {
    width: 40%;
    height: 40px;
    font-size: 14px;
  }

  .verify-code {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }

  .login-error {
    margin-bottom: 30px;
  }

  .login-btn {
    width: 100%;
    margin-bottom: 24px;
    margin-top: 6px;
  }
  .login-btn.is-disabled {
    background-color: var(--color-fill-disable);
  }

  .privacy-policy {
    font-weight: 400;
    font-size: 14px;
    color: var(--color-text-caption);

    /deep/ a {
      text-decoration: none;
    }
  }
}

.login-wrap {
  .login-form {
    width: 400px;
  }
}

.login-wrap-mobile {
  .login-form {
    padding: 0 40px;
  }
}

.login-copyright {
  position: absolute;
  bottom: 20px;
  color: #bbb;
  font-size: 12px;
  opacity: 0.8;
}
</style>



// WEBPACK FOOTER //
// src/pages/login/LoginForm.vue